import { createAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { Reminder } from '@app/features/reminder/types';
import {
  CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_FAILURE,
  CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_SUCCESS,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_FAILURE,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
  CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_FAILURE,
  CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_SUCCESS,
  CONTRACT_SETUP_ALL_STATE_CLEAR,
  CONTRACT_SETUP_ATTACHMENT_SAVE,
  CONTRACT_SETUP_BY_DRAFT_REQUEST,
  CONTRACT_SETUP_BY_DRAFT_SUCCESS,
  CONTRACT_SETUP_CLEAR_REMINDER,
  CONTRACT_SETUP_CONTRACT_DATA_UPDATE,
  CONTRACT_SETUP_CREATE_REMINDER,
  CONTRACT_SETUP_DELETE_REMINDER,
  CONTRACT_SETUP_DRAFT_FETCHED,
  CONTRACT_SETUP_EXPIRATION_DATE_CHANGE,
  CONTRACT_SETUP_FIELD__ALLOW_SIGNATURE_TYPES_SELECT,
  CONTRACT_SETUP_FIELD__CREATION_CANCEL,
  CONTRACT_SETUP_FIELD_CHECKBOX_GROUP_UPDATE_INDICES,
  CONTRACT_SETUP_FIELD_CONTEXT_MENU_CHANGE,
  CONTRACT_SETUP_FIELD_CREATE,
  CONTRACT_SETUP_FIELD_DATA_SET,
  CONTRACT_SETUP_FIELD_NOT_MODIFIED_GUIDE_CONFIRM,
  CONTRACT_SETUP_FIELD_POSITION_CHANGE,
  CONTRACT_SETUP_FIELD_REMOVE,
  CONTRACT_SETUP_FIELD_SIZE_CHANGE,
  CONTRACT_SETUP_FIELD_TARGET_CHANGE,
  CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE,
  CONTRACT_SETUP_FILE_OPEN_PASSWORD_CHANGE,
  CONTRACT_SETUP_FILENAME_CHANGE,
  CONTRACT_SETUP_IS_CHECK_SCHEDULE,
  CONTRACT_SETUP_LABEL_IDS_CHANGE,
  CONTRACT_SETUP_PARTICIPANT_AUTHENTICATION_CHANGE,
  CONTRACT_SETUP_PARTICIPANT_MESSAGE_CHANGE,
  CONTRACT_SETUP_PARTICIPANT_REMOVE,
  CONTRACT_SETUP_PDF_SET_EXIT_ROUTE,
  CONTRACT_SETUP_REQUIRED_VERIFICATION_CHANGE,
  CONTRACT_SETUP_SCHEDULE_SAVE,
  CONTRACT_SETUP_SUBMIT_CONFIGURATION,
  CONTRACT_SETUP_SUBMITTED,
  CONTRACT_SETUP_UPDATE_REMINDER,
} from '@app/store/actionTypes';
import AllowSignatureTypes from '@app/store/AllowSignatureTypes';
import AttachmentsSaveRequest from '@app/store/contract-setup/AttachmentsSaveRequest';
import ContractSetupFieldInformation from '@app/store/contract-setup/ContractSetupFieldInformation';
import ContractSetupFieldPropType from '@app/store/contract-setup/ContractSetupFieldPropType';
import ContractSetupMetaData from '@app/store/contract-setup/ContractSetupMetaData';
import { ContractSetupVerificationMethodMap } from '@app/store/contract-setup/ContractSetupVerificationMethodMap';
import ParticipantAuthentication from '@app/store/contract-setup/ParticipantAuthentication';
import ParticipantField from '@app/store/contract-setup/ParticipantField';
import { ContractSetupExitRouteState } from '@app/store/contract-setup/reducers/exitRoute';
import RequesterField from '@app/store/contract-setup/RequesterField';
import {
  getFieldIdsInPageMap,
  participantFieldIdsSelector,
} from '@app/store/contract-setup/selectors';
import Attachment from '@app/store/document/Attachment';
import DocumentsParticipant from '@app/store/documents/Participant';
import Draft from '@app/store/embedded/Draft';
import FieldType from '@app/store/FieldType';
import FontType from '@app/store/FontType';
import { HostReference } from '@app/store/Host';
import LocaleCode from '@app/store/LocaleCode';
import MeansType from '@app/store/MeansType';
import SigningProgressType from '@app/store/SigningProgressType';
import TextAlign from '@app/store/TextAlign';
import { RoleGrant } from '@app/store/transport-layer/document/documents';
import { Thunk } from '@app/store/types';

export const changeContractSetupSigningProgressType = createAction<
  SigningProgressType,
  'ContractSetup/changeSigningProgressType'
>('ContractSetup/changeSigningProgressType');

export function changeFileOpenPassword(payload: string) {
  return {
    type: CONTRACT_SETUP_FILE_OPEN_PASSWORD_CHANGE,
    payload,
  };
}

export function contractSetupParticipantAttachmentsSave(payload: {
  participantId: string;
  requests: AttachmentsSaveRequest[];
}) {
  return {
    type: CONTRACT_SETUP_ATTACHMENT_SAVE,
    payload,
  };
}

export const contractSetupSetParticipants = createAction<
  Array<{
    locale: LocaleCode;
    means: string;
    meansType: MeansType;
    name: string;
    participantId: string;
    host?: HostReference;
  }>,
  'contractSetup/contractSetupSetParticipants'
>('contractSetup/contractSetupSetParticipants');

export const contractSetupAllStateClear = createAction<undefined, 'CONTRACT_SETUP_ALL_STATE_CLEAR'>(
  CONTRACT_SETUP_ALL_STATE_CLEAR
);

export const contractSetupSubmitted = createAction<
  {
    signerCount: number;
    documentId: string;
    title: string;
    labelIds?: string[];
    fieldCounts: {
      REQUESTER: { [key: string]: number };
      SIGNER: { [key: string]: number };
      HOST: { [key: string]: number };
    };
    signingMethodTypes: string[];
    isInOrder: boolean | null;
  },
  typeof CONTRACT_SETUP_SUBMITTED
>(CONTRACT_SETUP_SUBMITTED);

/**
 * 최종적으로 문서 생성을 요청한다.
 */
export function contractSetupSubmitConfiguration(payload: {
  labelIds?: string[];
  roleGrants?: RoleGrant[];
}) {
  return {
    type: CONTRACT_SETUP_SUBMIT_CONFIGURATION,
    payload,
  };
}

export function contractSetupFieldCreate(payload: {
  meta: ContractSetupMetaData;
  information: ContractSetupFieldInformation;
}) {
  return {
    type: CONTRACT_SETUP_FIELD_CREATE,
    payload,
  };
}

export function contractSetupFieldDataSet(payload: {
  page: number;
  relativeX: number;
  relativeY: number;
}) {
  return {
    type: CONTRACT_SETUP_FIELD_DATA_SET,
    payload,
  };
}

export function confirmThatFieldNotModifiedGuide() {
  return {
    type: CONTRACT_SETUP_FIELD_NOT_MODIFIED_GUIDE_CONFIRM,
  };
}

export function contractSetupFieldRemove(uuid: string, participantId: string, page: number) {
  return {
    type: CONTRACT_SETUP_FIELD_REMOVE,
    payload: {
      page,
      participantId,
      uuid,
    },
  };
}

export function contractSetupExpirationDateChange(payload: number) {
  return {
    type: CONTRACT_SETUP_EXPIRATION_DATE_CHANGE,
    payload,
  };
}

export function contractSetupFieldContextMenuChange(
  id: string,
  propType: ContractSetupFieldPropType,
  value: FontType | TextAlign | boolean | number | string
) {
  return {
    type: CONTRACT_SETUP_FIELD_CONTEXT_MENU_CHANGE,
    payload: {
      id,
      propType,
      value,
    },
  };
}

export function contractSetupFieldSizeChange(
  id: string,
  relativeHeight: number,
  relativeWidth: number,
  currentScaleHeight: number,
  currentScaleWidth: number
) {
  return {
    type: CONTRACT_SETUP_FIELD_SIZE_CHANGE,
    payload: {
      currentScaleHeight,
      currentScaleWidth,
      id,
      relativeHeight,
      relativeWidth,
    },
  };
}

export function contractSetupFieldPositionChange(
  uuid: string,
  relativeX: number,
  relativeY: number
) {
  return {
    type: CONTRACT_SETUP_FIELD_POSITION_CHANGE,
    payload: {
      uuid,
      relativeX,
      relativeY,
    },
  };
}

export const contractSetupDeleteFieldsOverMaxDocumentPage = createAction<
  { fieldIds: string[]; pages: string[] },
  typeof CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE
>(CONTRACT_SETUP_FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE);

export const contractSetupParticipantRemove = createAction(
  CONTRACT_SETUP_PARTICIPANT_REMOVE,
  (participantId: string, participantFieldIds: string[]) => {
    return {
      payload: {
        participantId,
        participantFieldIds,
      },
    };
  }
);

export function contractSetupParticipantAuthenticationChange(payload: {
  digitalCertificate: {
    bizNumber?: string;
    companyName?: string;
  };
  mobile: {
    phoneNumber?: string;
    realName?: string;
  };
  participantId: string;
  password: {
    hint?: string;
    value?: string;
  };
}) {
  return {
    type: CONTRACT_SETUP_PARTICIPANT_AUTHENTICATION_CHANGE,
    payload,
  };
}

export function contractSetupContractDataUpdate(payload: {
  fileId: string;
  title: string;
  url: string;
}) {
  return {
    type: CONTRACT_SETUP_CONTRACT_DATA_UPDATE,
    payload,
  };
}

export const contractSetupDraftFetched = createAction<
  { keywords: string[] },
  typeof CONTRACT_SETUP_DRAFT_FETCHED
>(CONTRACT_SETUP_DRAFT_FETCHED);

/**
 * TODO: @Juyeong-Byeon
 * 아래 2개의 contractSetupByDraftRequest, contractSetupByDraftSuccess 제거하기
 */
export const contractSetupByDraftRequest = createAction<
  { draft: Draft },
  typeof CONTRACT_SETUP_BY_DRAFT_REQUEST
>(CONTRACT_SETUP_BY_DRAFT_REQUEST);

export const contractSetupByDraftSuccess = createAction<
  Draft,
  typeof CONTRACT_SETUP_BY_DRAFT_SUCCESS
>(CONTRACT_SETUP_BY_DRAFT_SUCCESS);

export function contractSetupFilenameChange(payload: string) {
  return {
    type: CONTRACT_SETUP_FILENAME_CHANGE,
    payload,
  };
}

export function contractSetupParticipantMessageChange(participantId: string, message: string) {
  return {
    type: CONTRACT_SETUP_PARTICIPANT_MESSAGE_CHANGE,
    payload: {
      message,
      participantId,
    },
  };
}

export function contractSetupFieldTargetChange(
  payload:
    | {
        participantId: string;
        type: FieldType.CHECKBOX_GROUP | FieldType.TEXT_FIELD;
      }
    | {
        allowSignatureTypes: AllowSignatureTypes;
        participantId: string;
        type: FieldType.SIGNATURE_FIELD;
      }
) {
  return {
    type: CONTRACT_SETUP_FIELD_TARGET_CHANGE,
    payload,
  };
}

export const contractSetupFieldCheckboxGroupIndicesUpdate = createAction(
  CONTRACT_SETUP_FIELD_CHECKBOX_GROUP_UPDATE_INDICES,
  (groupLabel: string, indicesById: { id: string; index: number }[]) => {
    return {
      payload: {
        groupLabel,
        indicesById,
      },
    };
  }
);

export const setExitRoute = createAction<
  ContractSetupExitRouteState,
  typeof CONTRACT_SETUP_PDF_SET_EXIT_ROUTE
>(CONTRACT_SETUP_PDF_SET_EXIT_ROUTE);

export function fetchDocumentAuthentications(payload: { documentId: string }) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_REQUEST,
    payload,
  };
}

export function fetchDocumentAuthenticationsSuccess(payload: {
  participantAuthentications: ParticipantAuthentication[];
}) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_SUCCESS,
    payload,
  };
}

export function fetchDocumentAuthenticationsFailure(error: AxiosError) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_AUTHENTICATIONS_FETCH_FAILURE,
    payload: error,
    error: true,
  };
}

export function fetchDocumentFields(payload: { documentId: string }) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
    payload,
  };
}

export function fetchDocumentFieldsSuccess(payload: {
  participantFields: ParticipantField[];
  requesterFields: RequesterField[];
  participantAttachments: Attachment[];
}) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
    payload,
  };
}

export function fetchDocumentFieldsFailure(error: AxiosError) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_FAILURE,
    payload: error,
    error: true,
  };
}

export function fetchDocumentParticipants(payload: { documentId: string }) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_REQUEST,
    payload,
  };
}

export function fetchDocumentParticipantsSuccess(payload: {
  participants: DocumentsParticipant[];
}) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_SUCCESS,
    payload,
  };
}

export function fetchDocumentParticipantsFailure(error: AxiosError) {
  return {
    type: CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_FAILURE,
    payload: error,
    error: true,
  };
}

export function contractSetupSelectAllowSignatureType(payload: {
  allowSignatureTypes: AllowSignatureTypes;
  participantId: string;
}) {
  return {
    type: CONTRACT_SETUP_FIELD__ALLOW_SIGNATURE_TYPES_SELECT,
    payload,
  };
}

export function contractSetupFieldCancelCreation() {
  return {
    type: CONTRACT_SETUP_FIELD__CREATION_CANCEL,
  };
}

export function contractSetupScheduleSave(payload: { scheduledDate: string | null }) {
  return {
    type: CONTRACT_SETUP_SCHEDULE_SAVE,
    payload,
  };
}

export function contractSetupSetIsCheckSchedule(payload: { isCheckSchedule: boolean }) {
  return {
    type: CONTRACT_SETUP_IS_CHECK_SCHEDULE,
    payload,
  };
}

export function contractSetupLabelIdsChange(payload: { labelIds: string[] }) {
  return {
    type: CONTRACT_SETUP_LABEL_IDS_CHANGE,
    payload,
  };
}

export function contractSetupRequiredVerificationChange(payload: {
  verifications: ContractSetupVerificationMethodMap[];
}) {
  return {
    type: CONTRACT_SETUP_REQUIRED_VERIFICATION_CHANGE,
    payload,
  };
}

export function contractSetupDeleteReminder(payload: { id: string }) {
  return {
    type: CONTRACT_SETUP_DELETE_REMINDER,
    payload,
  };
}

export function contractSetupCreateReminder(payload: Reminder) {
  return {
    type: CONTRACT_SETUP_CREATE_REMINDER,
    payload,
  };
}

export function contractSetupUpdateReminder(payload: Reminder) {
  return {
    type: CONTRACT_SETUP_UPDATE_REMINDER,
    payload,
  };
}

export function contractSetupClearReminder() {
  return {
    type: CONTRACT_SETUP_CLEAR_REMINDER,
  };
}

export type ContractSetupAction =
  | ReturnType<typeof changeContractSetupSigningProgressType>
  | ReturnType<typeof changeFileOpenPassword>
  | ReturnType<typeof confirmThatFieldNotModifiedGuide>
  | ReturnType<typeof contractSetupAllStateClear>
  | ReturnType<typeof contractSetupByDraftRequest>
  | ReturnType<typeof contractSetupByDraftSuccess>
  | ReturnType<typeof contractSetupContractDataUpdate>
  | ReturnType<typeof contractSetupExpirationDateChange>
  | ReturnType<typeof contractSetupFieldCancelCreation>
  | ReturnType<typeof contractSetupFieldContextMenuChange>
  | ReturnType<typeof contractSetupFieldCreate>
  | ReturnType<typeof contractSetupFieldDataSet>
  | ReturnType<typeof contractSetupFieldPositionChange>
  | ReturnType<typeof contractSetupFieldRemove>
  | ReturnType<typeof contractSetupFieldSizeChange>
  | ReturnType<typeof contractSetupFieldTargetChange>
  | ReturnType<typeof contractSetupDeleteFieldsOverMaxDocumentPage>
  | ReturnType<typeof contractSetupFilenameChange>
  | ReturnType<typeof contractSetupFieldCheckboxGroupIndicesUpdate>
  | ReturnType<typeof contractSetupParticipantAttachmentsSave>
  | ReturnType<typeof contractSetupParticipantAuthenticationChange>
  | ReturnType<typeof contractSetupParticipantMessageChange>
  | ReturnType<typeof contractSetupParticipantRemove>
  | ReturnType<typeof contractSetupSelectAllowSignatureType>
  | ReturnType<typeof contractSetupSetParticipants>
  | ReturnType<typeof contractSetupSubmitted>
  | ReturnType<typeof contractSetupSubmitConfiguration>
  | ReturnType<typeof fetchDocumentAuthentications>
  | ReturnType<typeof fetchDocumentAuthenticationsSuccess>
  | ReturnType<typeof fetchDocumentAuthenticationsFailure>
  | ReturnType<typeof fetchDocumentFields>
  | ReturnType<typeof fetchDocumentFieldsSuccess>
  | ReturnType<typeof fetchDocumentFieldsFailure>
  | ReturnType<typeof fetchDocumentParticipants>
  | ReturnType<typeof fetchDocumentParticipantsSuccess>
  | ReturnType<typeof fetchDocumentParticipantsFailure>
  | ReturnType<typeof setExitRoute>
  | ReturnType<typeof contractSetupScheduleSave>
  | ReturnType<typeof contractSetupSetIsCheckSchedule>
  | ReturnType<typeof contractSetupLabelIdsChange>
  | ReturnType<typeof contractSetupRequiredVerificationChange>
  | ReturnType<typeof contractSetupCreateReminder>
  | ReturnType<typeof contractSetupDeleteReminder>
  | ReturnType<typeof contractSetupUpdateReminder>
  | ReturnType<typeof contractSetupClearReminder>;

/**
 * 서명자 삭제를 진행한다.
 */
export const removeParticipant =
  (participantId: string): Thunk =>
  (dispatch, getState) => {
    const participantFieldIds = participantFieldIdsSelector(getState());
    const specificParticipantFieldIds = participantFieldIds[participantId];

    dispatch(contractSetupParticipantRemove(participantId, specificParticipantFieldIds));
  };

/**
 * PDF 파일을 교체하여 페이지 수가 변경되고, 현재 PDF 페이지 범위 밖에 필드 정보가 있는경우
 * 해당 필드 정보를 삭제합니다.
 */
export const deletePageOverParticipantFields =
  ({ maxPage }: { maxPage: number }): Thunk =>
  (dispatch, getState) => {
    const fieldIdsInPages = getFieldIdsInPageMap(getState());
    const fieldPages = Object.keys(fieldIdsInPages).map((page) => parseInt(page, 10) ?? 0);

    // 서명자 필드 지우기
    const deleteFieldIds = Object.entries(fieldIdsInPages)
      .filter(([page, _ids]) => (parseInt(page, 10) ?? 0) > maxPage)
      .map(([_page, ids]) => ids)
      .flat();
    const deletePages = fieldPages.filter((page) => page > maxPage).map((page) => page.toString());
    dispatch(
      contractSetupDeleteFieldsOverMaxDocumentPage({ fieldIds: deleteFieldIds, pages: deletePages })
    );
  };
